// The LiveOps cards (spec/dashboard.md sections 7.2–7.3): the game event, the simple offer and the offer group as header
// chips and bodies over the shared widgets; an attached offer renders inline inside its event.
import { useAssetUrls, useBranchAssetCtx } from '../../api/assetUrls';
import { useState, type ReactNode } from 'react';
import type { Document, Json, Parameter } from '../../api/types';
import { useContent } from '../../content/ContentContext';
import { Chip } from '../../content/cards/Chips';
import type { CardContext } from '../../content/cards/CardList';
import { ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { formatValue } from '../../content/format';
import { parameterHelp } from '../../content/help';
import { n, useDefinitionText, useEntityFigures, type EntityKind } from '../../content/liveops/analytics';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { createDocumentDraft, joinPath, useEditorHost, useWidgetEnv } from '../../content/widgets/context';
import { Badge } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { CornerDownRight, Filter, User } from '../../ui/icons';
import { CommitTextInput, Select } from '../../ui/inputs';
import { TagInput } from '../../ui/values';
import { useWorkspaceVersion } from '../../workspace/react';
import { entityKey, isTempId } from '../../workspace/Workspace';
import './liveops.css';

export const EVENT = 'SmartObjects.GameEvent';
export const OFFER = 'SmartObjects.GameOffer';
export const GROUP = 'SmartObjects.GameOfferGroup';
export const STORE_ITEM = 'SmartObjects.StoreItem';

export function paramsOf(template: string): Parameter[] {
  const catalog = useContent();
  return catalog.templates.get(template)?.effectiveParameters ?? [];
}

function data(doc: Document): Record<string, Json> {
  return (doc.data ?? {}) as Record<string, Json>;
}

/** The chips of a document: `names` in order, each a parameter of the template; zero words per name. */
export function Chips({ ctx, params, names, zero = {}, hide = [] }: { ctx: CardContext; params: Parameter[]; names: string[]; zero?: Record<string, string>; hide?: string[] }) {
  const changed = new Set((ctx.draft?.changes ?? []).map((c) => c.path.replace(/^data\./, '').split(/[.[]/)[0]));
  return (
    <span className="card-chips">
      {names
        .filter((n) => !hide.includes(n))
        .map((n) => {
          const p = params.find((x) => x.name === n);
          return p ? <Chip key={n} doc={ctx.doc} param={p} onCommit={(v) => ctx.edit(`data.${n}`, v)} disabled={!ctx.canWrite} zeroMeans={zero[n]} changed={changed.has(n)} /> : null;
        })}
    </span>
  );
}

/** A labelled field of the body. */
export function BodyField({ ctx, params, name, help }: { ctx: CardContext; params: Parameter[]; name: string; help?: string }) {
  const p = params.find((x) => x.name === name);
  if (!p) return null;
  return (
    <Field label={p.displayName ?? p.name} help={help ?? parameterHelp(p, ctx.doc)}>
      <FieldWidget param={p} value={data(ctx.doc)[name]} disabled={!ctx.canWrite} owner={ctx.doc.id} onCommit={(v) => ctx.edit(`data.${name}`, v)} onCommitAt={(rel, v) => ctx.edit(joinPath(`data.${name}`, rel), v)} />
    </Field>
  );
}

export function CommonTail({ ctx }: { ctx: CardContext }) {
  const catalog = useContent();
  const meta = (ctx.doc.meta ?? {}) as Record<string, Json>;
  return (
    <>
      <Field label="Comments" help="Editor-only notes; never released">
        <CommitTextInput multiline value={typeof meta.notes === 'string' ? meta.notes : ''} disabled={!ctx.canWrite} aria-label="Comments" onCommit={(v) => ctx.edit('meta', v ? { ...meta, notes: v } : Object.keys(meta).length > 1 ? Object.fromEntries(Object.entries(meta).filter(([k]) => k !== 'notes')) : null)} />
      </Field>
      <Field label="Tags">
        <TagInput id={`tags-${ctx.doc.id}`} value={ctx.doc.tags} suggestions={catalog.tags.map((t) => t.name)} disabled={!ctx.canWrite} onChange={(tags) => ctx.edit('tags', tags)} />
      </Field>
    </>
  );
}

export function NameText({ doc }: { doc: Document }) {
  const catalog = useContent();
  const name = data(doc).name;
  const text = typeof name === 'string' ? catalog.stringText(name) : undefined;
  return <span className="card-title">{text ?? catalog.label(doc)}</span>;
}

export function IconThumb({ doc, field = 'unnyIcon' }: { doc: Document; field?: string }) {
  const catalog = useContent();
  const id = data(doc)[field];
  const asset = typeof id === 'string' ? catalog.assets.get(id) : undefined;
  // a signed URL (M7 slice 3): the public path answers 404 until a pinned release lists the asset
  const src = useAssetUrls(useBranchAssetCtx(), asset?.contentPath ? [asset.id] : [])[asset?.id ?? ''];
  return asset?.contentPath && src ? <img className="card-icon" src={src} alt="" /> : <span className="card-icon placeholder" aria-hidden="true" />;
}

/** A card's condition summary on the header line: a muted filter icon, the sentence, the time status. */
function CondText({ id }: { id: Json | undefined }) {
  return (
    <span className="lo-cond">
      <Filter aria-hidden="true" />
      <ConditionSummary rootId={typeof id === 'string' ? id : null} />
    </span>
  );
}

/** A titled group of the expanded body; `single` hides the lone field's own label visually (the title says it). */
function Group({ title, single, children }: { title: string; single?: boolean; children: ReactNode }) {
  return (
    <section className={`lo-group ${single ? 'single' : ''}`} aria-label={title}>
      <h4 className="lo-group-title">{title}</h4>
      {children}
    </section>
  );
}

/**
 * The users badge of an event / offer / offer group card (M7 slice 7): total · 30 d · 7 d by the players' last activity on
 * it in the selected environment, the definitions and "computed <time>" in the tooltip (Q-J-12).
 */
export function UsersBadge({ kind, id }: { kind: EntityKind; id: string }) {
  const f = useEntityFigures(kind, [id]);
  const define = useDefinitionText();
  if (isTempId(id)) return null;
  const item = f.get(id);
  if (!item) {
    return (
      <span data-testid={`users-${id}`}>
        <Badge title={f.error ? `Users unavailable: ${f.error.message}` : 'Loading the users of the selected environment…'}><User className="icon-s" aria-hidden="true" /> {f.error ? '—' : '…'}</Badge>
      </span>
    );
  }
  const u = item.users;
  const title = define(f.definitions, ['total', 'activeUsers', 'testers'], f.computedAt, [`${n(u.total)} players ever active on it; ${n(u.month)} last active within 30 days, ${n(u.week)} within 7 days (selected environment)`]);
  return (
    <span data-testid={`users-${id}`}>
      <Badge tone={n(u.total) > 0 ? 'info' : 'neutral'} title={title}>
        <User className="icon-s" aria-hidden="true" /> {n(u.total)} · 30 d {n(u.month)} · 7 d {n(u.week)}
      </Badge>
    </span>
  );
}

// ---- game events ----------------------------------------------------------------------------------------------------

export function EventHeader({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(ctx.doc.template);
  const catalog = useContent();
  const d = data(ctx.doc);
  const heir = ctx.doc.template !== EVENT;
  return (
    <div className="card-head-lines">
      <div className="lo-head-line">
        <IconThumb doc={ctx.doc} />
        <NameText doc={ctx.doc} />
        {heir && <Badge>{catalog.templates.get(ctx.doc.template)?.displayName ?? ctx.doc.template.split('.').pop()}</Badge>}
        <UsersBadge kind="event" id={ctx.doc.id} />
        <CondText id={d.condition} />
      </div>
      <Chips ctx={ctx} params={params} names={['unnyPriority', 'unnyPlacement', 'finishType', 'duration', 'cooldown', 'limit', 'manualRemove']} zero={{ duration: 'infinite', cooldown: 'no cooldown', limit: 'unlimited' }} hide={Number(d.finishType ?? 0) === 1 ? [] : ['duration']} />
    </div>
  );
}

export function EventBody({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(ctx.doc.template);
  const catalog = useContent();
  const own = params.filter((p) => !['unnyIcon', 'name', 'script', 'condition', 'finishType', 'duration', 'unnyPriority', 'unnyView', 'unnyPlacement', 'cooldown', 'limit', 'manualRemove'].includes(p.name));
  const heir = ctx.doc.template !== EVENT ? catalog.templates.get(ctx.doc.template) : undefined;
  return (
    <div className="card-fields lo-body">
      <div className="card-fields-main">
        <Group title="Event">
          <BodyField ctx={ctx} params={params} name="name" />
        </Group>
        <Group title="Attachment" single>
          <AttachmentField ctx={ctx} params={params} />
        </Group>
        <Group title="Condition" single>
          <BodyField ctx={ctx} params={params} name="condition" />
        </Group>
        {own.length > 0 && (
          <Group title={heir ? `${heir.displayName ?? heir.name.split('.').pop()} fields` : 'More fields'}>
            {own.map((p) => (
              <BodyField key={p.name} ctx={ctx} params={params} name={p.name} />
            ))}
          </Group>
        )}
        <Group title="Notes">
          <CommonTail ctx={ctx} />
        </Group>
      </div>
      <div className="card-fields-side">
        <Group title="Display">
          <BodyField ctx={ctx} params={params} name="unnyView" />
          <BodyField ctx={ctx} params={params} name="unnyIcon" />
        </Group>
      </div>
    </div>
  );
}

const ATTACHMENTS: { value: string; label: string }[] = [
  { value: OFFER, label: 'Game offer' },
  { value: GROUP, label: 'Game offer group' },
  { value: 'VisualScripting.ScriptNode', label: 'Script (retired)' },
];

/** The event's polymorphic attachment: an offer, an offer group or a script, with the attached offer inline. */
export function AttachmentField({ ctx, params }: { ctx: CardContext; params: Parameter[] }) {
  const catalog = useContent();
  const { workspace } = useWidgetEnv();
  useWorkspaceVersion(workspace);
  const p = params.find((x) => x.name === 'script');
  const current = data(ctx.doc).script;
  const target = typeof current === 'string' ? catalog.resolve(current) : undefined;
  const [kind, setKind] = useState<string>(target?.template ?? OFFER);
  if (!p) return null;
  const chosen = target?.template && catalog.templates.has(target.template) ? target.template : kind;
  return (
    <Field label="Attachment" help="What the event activates with itself: an offer or an offer group (scripts are retired)">
      <div className="stack">
        <div className="lo-attachment-kind">
          <Select size="s" value={chosen} disabled={!ctx.canWrite || !!target} aria-label="Attachment kind" onChange={(e) => setKind(e.target.value)}>
            {ATTACHMENTS.filter((a) => catalog.templates.has(a.value) || a.value === chosen).map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
          <FieldWidget param={{ ...p, template: chosen, displayName: 'Attachment' }} value={current} disabled={!ctx.canWrite} owner={ctx.doc.id} onCommit={(v) => ctx.edit('data.script', v)} />
        </div>
        {target && target.template === OFFER && <InlineOffer id={target.id} canWrite={ctx.canWrite} />}
        {target && target.template === GROUP && <InlineGroup id={target.id} canWrite={ctx.canWrite} />}
      </div>
    </Field>
  );
}

function useInlineCtx(id: string, canWrite: boolean): CardContext | null {
  const { catalog, workspace } = useWidgetEnv();
  useWorkspaceVersion(workspace);
  const host = useEditorHost();
  const draft = workspace.getDraft(entityKey('document', id));
  const doc = (draft?.current as unknown as Document | undefined) ?? (catalog.resolve(id) as Document | undefined);
  if (!doc) return null;
  const server = catalog.resolver.get(id) as Document | undefined;
  return {
    doc,
    draft,
    canWrite: canWrite && draft?.status !== 'deleted',
    expanded: true,
    open: () => void host?.open(id),
    edit: (path, value) => workspace.edit('document', id, path, value, { base: server ? { version: Number(server.version), value: server as unknown as Json } : undefined, label: catalog.label(doc), template: doc.template }),
  };
}

function Attached({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <div className="lo-attached">
      <span className="lo-attached-caption">
        <CornerDownRight aria-hidden="true" /> {caption}
      </span>
      {children}
    </div>
  );
}

function AttachedSkeleton({ caption }: { caption: string }) {
  return (
    <Attached caption={caption}>
      <div className="inline-card" role="status" aria-label={`Loading the ${caption.toLowerCase()}`}>
        <span className="skeleton" />
        <span className="skeleton" />
      </div>
    </Attached>
  );
}

export function InlineOffer({ id, canWrite }: { id: string; canWrite: boolean }) {
  const ctx = useInlineCtx(id, canWrite);
  if (!ctx) return <AttachedSkeleton caption="Attached offer" />;
  return (
    <Attached caption="Attached offer">
      <div className="inline-card" data-testid={`inline-offer-${id}`}>
        <OfferHeader ctx={ctx} />
        <OfferBody ctx={ctx} />
      </div>
    </Attached>
  );
}

export function InlineGroup({ id, canWrite }: { id: string; canWrite: boolean }) {
  const ctx = useInlineCtx(id, canWrite);
  if (!ctx) return <AttachedSkeleton caption="Attached offer group" />;
  return (
    <Attached caption="Attached offer group">
      <div className="inline-card" data-testid={`inline-group-${id}`}>
        <GroupHeader ctx={ctx} />
        <GroupBody ctx={ctx} />
      </div>
    </Attached>
  );
}

// ---- offers -----------------------------------------------------------------------------------------------------------

export function OfferHeader({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(ctx.doc.template);
  return (
    <div className="card-head-lines">
      <div className="lo-head-line">
        <IconThumb doc={ctx.doc} field="icon" />
        <NameText doc={ctx.doc} />
        <UsersBadge kind="offer" id={ctx.doc.id} />
      </div>
      <Chips ctx={ctx} params={params} names={['unnyPriority', 'sprite', 'unnyPlacement', 'wait', 'duration', 'limit', 'cooldown', 'reactivate', 'lifetimeFrom']} zero={{ duration: 'ends with the event', limit: 'unlimited', cooldown: 'none' }} />
    </div>
  );
}

export function OfferBody({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(ctx.doc.template);
  return (
    <div className="card-fields lo-body">
      <div className="card-fields-main">
        <Group title="Offer">
          <BodyField ctx={ctx} params={params} name="name" />
          <BodyField ctx={ctx} params={params} name="description" />
        </Group>
        <Group title="Store item and price">
          <BodyField ctx={ctx} params={params} name="storeItem" />
          <StoreItemPreview id={data(ctx.doc).storeItem} />
        </Group>
        <Group title="Notes">
          <CommonTail ctx={ctx} />
        </Group>
      </div>
      <div className="card-fields-side">
        <Group title="Display">
          <BodyField ctx={ctx} params={params} name="unnyView" />
          <BodyField ctx={ctx} params={params} name="icon" />
          <BodyField ctx={ctx} params={params} name="sprite" />
        </Group>
      </div>
    </div>
  );
}

/** The read-only preview of a store item's price and reward under an offer. */
export function StoreItemPreview({ id }: { id: Json | undefined }) {
  const catalog = useContent();
  const item = typeof id === 'string' ? catalog.resolve(id) : undefined;
  const params = catalog.templates.get(STORE_ITEM)?.effectiveParameters ?? [];
  if (!item) return null;
  const d = (item.data ?? {}) as Record<string, Json>;
  const price = params.find((p) => p.name === 'price');
  const reward = params.find((p) => p.name === 'reward');
  return (
    <dl className="kv small store-item-preview lo-preview">
      <dt>Price</dt>
      <dd>{price ? formatValue(price, d.price, catalog.format) : '—'}</dd>
      <dt>Reward</dt>
      <dd>{reward ? formatValue(reward, d.reward, catalog.format) : '—'}</dd>
    </dl>
  );
}

export function GroupHeader({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(ctx.doc.template);
  const items = data(ctx.doc).storeItems;
  return (
    <div className="card-head-lines">
      <div className="lo-head-line">
        <IconThumb doc={ctx.doc} field="icon" />
        <NameText doc={ctx.doc} />
        <Badge>{Array.isArray(items) ? `${items.length} store item${items.length === 1 ? '' : 's'}` : '0 store items'}</Badge>
        <UsersBadge kind="offer_group" id={ctx.doc.id} />
      </div>
      <Chips ctx={ctx} params={params} names={['type', 'duration', 'unnyPlacement', 'wait', 'unnyPriority']} zero={{ duration: 'ends with the event' }} />
    </div>
  );
}

export function GroupBody({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(ctx.doc.template);
  return (
    <div className="card-fields lo-body">
      <div className="card-fields-main">
        <Group title="Offer group">
          <BodyField ctx={ctx} params={params} name="name" />
        </Group>
        <Group title="Store items">
          <BodyField ctx={ctx} params={params} name="storeItems" help="The order is the chain order" />
        </Group>
        <Group title="Notes">
          <CommonTail ctx={ctx} />
        </Group>
      </div>
      <div className="card-fields-side">
        <Group title="Display">
          <BodyField ctx={ctx} params={params} name="unnyView" />
          <BodyField ctx={ctx} params={params} name="icon" />
        </Group>
      </div>
    </div>
  );
}

/** A new document of a template with the given values, staged. */
export function stage(workspace: ReturnType<typeof useWidgetEnv>['workspace'], catalog: ReturnType<typeof useContent>, template: string, values: Record<string, Json>): string {
  const def = catalog.templates.get(template);
  if (!def) throw new Error(`No template ${template} on this branch`);
  return createDocumentDraft(workspace, def, values);
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="lo-group" aria-label={title}>
      <h3 className="lo-group-title">{title}</h3>
      {children}
    </section>
  );
}
